import type { LocationTypeData, RigData, SpeciesData, TechniqueData } from '../../types';
import { inferSpeciesTargetability } from '../../utils/speciesTargetability';
import { isTechniqueManualReviewHold, type ResolvedRecommendationCatalog } from '../recommendations/catalog';

export type BeginnerRouteQualityIssue =
  | 'missing_component'
  | 'species_not_targetable'
  | 'technique_not_primary'
  | 'technique_not_beginner'
  | 'technique_not_linked'
  | 'technique_on_review_hold'
  | 'rig_not_primary'
  | 'rig_not_beginner'
  | 'rig_not_linked'
  | 'location_not_primary'
  | 'location_not_shore_friendly'
  | 'location_not_accessible'
  | 'location_species_not_linked'
  | 'location_technique_not_linked';

export interface BeginnerRouteQualityInput {
  species: SpeciesData | null | undefined;
  technique: TechniqueData | null | undefined;
  rig: RigData | null | undefined;
  location: LocationTypeData | null | undefined;
}

export interface BeginnerRouteQualityResult {
  isStrictBeginnerRoute: boolean;
  issues: BeginnerRouteQualityIssue[];
}

const SHORE_FRIENDLY_LOCATION_CATEGORIES = new Set<LocationTypeData['category']>([
  'coastal',
  'harbor',
  'estuary'
]);

const BEGINNER_ACCESSIBILITY = new Set<LocationTypeData['accessibility']>([
  'easy',
  'moderate'
]);

export const evaluateBeginnerRouteQuality = ({
  species,
  technique,
  rig,
  location
}: BeginnerRouteQualityInput): BeginnerRouteQualityResult => {
  if (!species || !technique || !rig || !location) {
    return { isStrictBeginnerRoute: false, issues: ['missing_component'] };
  }

  const issues: BeginnerRouteQualityIssue[] = [];

  if (inferSpeciesTargetability(species) !== 'targetable') {
    issues.push('species_not_targetable');
  }

  if (species.primaryBeginnerTechniqueId !== technique.techniqueId) {
    issues.push('technique_not_primary');
  }

  if (technique.difficulty !== 'beginner') {
    issues.push('technique_not_beginner');
  }

  if (!species.fishingTechniqueIds.includes(technique.techniqueId)) {
    issues.push('technique_not_linked');
  }

  if (isTechniqueManualReviewHold(technique.techniqueId)) {
    issues.push('technique_on_review_hold');
  }

  if (technique.primaryBeginnerRigId !== rig.rigId) {
    issues.push('rig_not_primary');
  }

  if (rig.difficulty !== 'beginner') {
    issues.push('rig_not_beginner');
  }

  if (!rig.suitableTechniqueIds.includes(technique.techniqueId)) {
    issues.push('rig_not_linked');
  }

  if (species.primaryBeginnerLocationTypeId !== location.locationTypeId) {
    issues.push('location_not_primary');
  }

  if (!SHORE_FRIENDLY_LOCATION_CATEGORIES.has(location.category)) {
    issues.push('location_not_shore_friendly');
  }

  if (!BEGINNER_ACCESSIBILITY.has(location.accessibility)) {
    issues.push('location_not_accessible');
  }

  if (!location.commonSpeciesIds.includes(species.speciesId)) {
    issues.push('location_species_not_linked');
  }

  if (
    !location.recommendedTechniqueIds.includes(technique.techniqueId) &&
    location.primaryBeginnerTechniqueId !== technique.techniqueId
  ) {
    issues.push('location_technique_not_linked');
  }

  return {
    isStrictBeginnerRoute: issues.length === 0,
    issues
  };
};

export const isStrictBeginnerRoute = (input: BeginnerRouteQualityInput): boolean =>
  evaluateBeginnerRouteQuality(input).isStrictBeginnerRoute;

export const hasReviewedBeginnerSetup = (
  input: BeginnerRouteQualityInput,
  catalog: ResolvedRecommendationCatalog | null | undefined
): boolean => {
  if (!isStrictBeginnerRoute(input) || !catalog) {
    return false;
  }

  const completeSetup = catalog.bundle.completeSetup;
  const techniqueMatch = `technique:${input.technique?.techniqueId || ''}`;
  const rigMatch = `rig:${input.rig?.rigId || ''}`;

  return Boolean(
    catalog.sourceType === 'curated_catalog' &&
    catalog.bundle.reviewStatus === 'curated' &&
    catalog.matchedBy.includes(techniqueMatch) &&
    catalog.matchedBy.includes(rigMatch) &&
    catalog.matchedBy.includes('access:shore') &&
    completeSetup?.reviewed &&
    completeSetup.items.length > 0 &&
    completeSetup.items.every((item) => item.reviewed)
  );
};

export const hasReviewedBeginnerCommerce = (
  input: BeginnerRouteQualityInput,
  catalog: ResolvedRecommendationCatalog | null | undefined
): boolean => Boolean(
  hasReviewedBeginnerSetup(input, catalog) && catalog?.bundle.commerceEnabled
);
